import { useDialog } from 'contexts/dialog';
import { useSnackbar } from 'contexts/snackbar';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, IconButton, RichTextArea, Stack, styled, Text } from 'shell/components';
import { stripHTML } from 'utils/helper';

const LegacyFeedbackRenderer = ({ currentSubmission, form, setForm, setShowFailedDialog }) => {
    const { setDialog, closeDialog } = useDialog();
    const { message } = useSnackbar();
    const { t } = useTranslation('assignment');

    const [isEditing, setIsEditing] = useState(false);
    const [value, setValue] = useState(form.feedback || '');

    const handleClickEdit = () => {
        setIsEditing(true);
        setValue(form.feedback || '');
    };

    const handleClickCopy = async () => {
        await navigator.clipboard.writeText(stripHTML(value));
        message.success(t('common:message.copied-to-clipboard', 'Copied to clipboard.'));
    };

    const handleClickDelete = () => {
        setDialog({
            open: true,
            message: t('dialog.delete-feedback.title', 'Delete Feedback?'),
            description: t(
                'dialog.delete-feedback.description',
                'Are you sure you want to delete this feedback? This can’t be undone.'
            ),
            note: '',
            variant: 'destructive',
            actionButtonText: t('common:controls.delete', 'Delete'),
            cancelButtonText: t('common:controls.cancel', 'Cancel'),
            onAction: () => {
                setForm((current) => ({ ...current, feedback: '' }));
                closeDialog();
            },
            onCancel: () => closeDialog(),
            onClose: () => closeDialog(),
        });
    };

    const handleClickSave = () => {
        setForm((current) => ({ ...current, feedback: value }));
        setIsEditing(false);
    };

    const handleClickCancel = () => {
        setIsEditing(false);
        setValue(form.feedback);
    };

    useEffect(() => {
        setIsEditing(false);
        setValue(form.feedback || '');
    }, [currentSubmission.id, form.feedback]);

    return (
        <>
            <BackgroundStack alignItems={'center'} direction={'row'} justifyContent={'space-between'}>
                <Text variant={'p1'} weight={'semibold'}>
                    {t('feedback-section.feedback-label', 'Feedback')}
                </Text>
                <Stack direction={'row'} spacing={2}>
                    <IconButton
                        aria-label={!form.feedback ? 'add feedback' : 'edit feedback'}
                        kind={!form.feedback ? 'add' : 'edit'}
                        size={'small'}
                        tooltip={{
                            component: !form.feedback
                                ? t('common:controls.add', 'Add')
                                : t('common:controls.edit', 'Edit'),
                        }}
                        variant={'square'}
                        onClick={handleClickEdit}
                    />
                    <IconButton
                        aria-label={'copy feedback'}
                        disabled={!form.feedback}
                        kind={'copy'}
                        size={'small'}
                        tooltip={{ component: t('common:controls.copy', 'Copy') }}
                        variant={'square'}
                        onClick={handleClickCopy}
                    />
                    <IconButton
                        aria-label={'delete feedback'}
                        disabled={!form.feedback}
                        kind={'delete'}
                        size={'small'}
                        tooltip={{ component: t('common:controls.delete', 'Delete') }}
                        variant={'square'}
                        onClick={handleClickDelete}
                    />
                </Stack>
            </BackgroundStack>
            <Stack sx={{ py: 4, px: 5 }} spacing={4}>
                {currentSubmission.is_failed && (
                    <Stack
                        sx={(theme) => ({
                            borderRadius: '8px',
                            p: theme.spacing(2, 3),
                            background: theme.palette.ensightful.warning[100],
                        })}
                        alignItems={'flex-start'}
                        spacing={2}
                    >
                        <Text color={(theme) => theme.palette.ensightful.warning[900]}>
                            {t(
                                'feedback-section.feedback-generation-failed',
                                'We were unable to generate feedback for this assignment. You can try to regenerate, or provide feedback manually.'
                            )}
                        </Text>
                        {setShowFailedDialog && (
                            <Button variant={'borderless'} onClick={() => setShowFailedDialog(true)}>
                                {t('common:ui.learn-more', 'Learn more')}
                            </Button>
                        )}
                    </Stack>
                )}
                {!isEditing ? (
                    <>
                        {!currentSubmission.is_failed && !form.feedback ? (
                            <Text sx={{ transform: 'translateY(30vh)' }} align={'center'} color={'light'}>
                                {t('feedback-section.feedback-deleted', 'This feedback was deleted')}
                            </Text>
                        ) : (
                            <Text sx={{ pb: 8 }} rich>
                                {form.feedback}
                            </Text>
                        )}
                    </>
                ) : (
                    <Stack spacing={3}>
                        <CustomRichTextArea
                            isFailed={currentSubmission.is_failed}
                            placeholder={t('feedback-section.feedback-placeholder', 'Write your feedback here')}
                            value={value}
                            onChange={setValue}
                        />
                        <Stack direction={'row-reverse'} spacing={3}>
                            <Button onClick={handleClickSave}>{t('common:controls.save', 'Save')}</Button>
                            <Button color={'neutral'} variant={'tertiary'} onClick={handleClickCancel}>
                                {t('common:controls.cancel', 'Cancel')}
                            </Button>
                        </Stack>
                    </Stack>
                )}
            </Stack>
        </>
    );
};

const BackgroundStack = styled(Stack)(({ theme }) => {
    return {
        width: '100%',
        padding: theme.spacing(2, 5),
        background: theme.palette.ensightful.primary[50],
    };
});

const CustomRichTextArea = styled(RichTextArea)(({ isFailed }) => {
    return {
        height: isFailed ? 'calc(100vh - 373px)' : 'calc(100vh - 269px)',
        '& > .MuiRichTextArea-root': {
            minHeight: '100%',
            maxHeight: '100%',
        },
    };
});

export default LegacyFeedbackRenderer;
